import type { App as VueApp } from 'vue';
import { setupStores } from './setupStores';
import { setupRouter } from './setupRouter';
import { setupRuntime } from './setupRuntime';
import router from '@Router/index';
import { verifyActivitySession, redirectToNotFound, setLocation, refreshUserIdentity, getCompanySlug } from 'host/mfeBridge';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import { useRequestFlowStore } from '@Stores/requestFlow.store';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import { addRequestDetailsToActivitySessionByRecipientTokenApi, validateAndApplyQrSessionTokenApi } from '@Api/activitySession.api';
import { checkInviteRedirectApi } from '@Api/inviteRedirect.api';
import { RequestType } from '@Enums/requestType.enum';
import { isFlowContinuationRoute, shouldSkipInviteDashboardRedirect } from './inviteDashboardRedirect';

const REQUEST_TYPE_MAP: Record<string, RequestType> = {
    send: RequestType.Send,
    confirm: RequestType.Confirm,
    collect: RequestType.Collect,
    identity: RequestType.Identity,
    unknown: RequestType.Unknown,
};

interface ResolvedEntry {
    token: string;
    requestType: RequestType | null;
    requestTypeParam: string;
    qrSessionToken: string;
    fromDashboard: boolean;
    enteredViaStartup: boolean;
    // PS-955: true when this mount is an Intellicheck capture callback
    // (match-idv-processing-complete / -verification-error) rather than a fresh
    // invite-link visit — the dashboard redirect must not fire here.
    isFlowContinuation: boolean;
}

export const setupApp = async (app: VueApp<Element>) => {
    setupStores(app);
    setupRuntime(app);

    await initializeMfe();

    setupRouter(app);       // install router AFTER initializeMfe
};

const initializeMfe = async () => {
    const verified = await ensureActivitySession();
    if (!verified) return;

    try {
        const entry = resolveEntryFromLocation();
        await applyQrSessionTokenIfAny(entry);

        if (entry.token) {
            const redirected = await handleInviteEntry(entry);
            if (redirected) return;
        }

        await loadAndPublishLocation();
    } catch (error) {
        console.error('[Request MFE] Failed to initialize MFE:', error);
        redirectToNotFound();
    }
};

/**
 * Confirms the host-side activity session is valid. On failure, tries one
 * self-heal via the persisted inviteToken before falling through to host's
 * not-found (DS-2779 B.2 — without this, a refresh that raced a slow
 * router-guard sync would land here permanently even when the invite is
 * still valid).
 *
 * @returns `true` when the caller should continue initialization, `false`
 * when the caller should bail out (redirect already issued).
 */
const ensureActivitySession = async (): Promise<boolean> => {
    try {
        await verifyActivitySession();
        return true;
    } catch (verifyError) {
        const recovered = await tryRecoverFromPersistedInviteToken();
        if (recovered) {
            console.log('[Request MFE] Recovered activity session via persisted invite token (DS-2779)');
            return true;
        }
        console.error('[Request MFE] verifyActivitySession failed and no invite-token recovery was possible:', verifyError);
        redirectToNotFound();
        return false;
    }
};

/**
 * DS-2779 B.2 — replay the persisted invite token against
 * addRequestDetailsToActivitySessionByRecipientTokenApi so a refreshed page
 * mid-flow can re-anchor without forcing the user to chase the original RCS
 * link. Returns `true` only when the BFF acknowledged the re-apply.
 */
const tryRecoverFromPersistedInviteToken = async (): Promise<boolean> => {
    const requestSessionStore = useRequestSessionStore();
    const inviteToken = requestSessionStore.inviteToken;
    if (!inviteToken) return false;

    const requestType = inferRequestTypeFromUrl();
    if (!requestType) return false;

    try {
        // addRequestDetailsToActivitySessionByRecipientTokenApi fully re-anchors the
        // host-side activity session context (cookies, headers, session identity), so a
        // second verifyActivitySession() call is not needed after this succeeds — the
        // session is valid by construction once the API acknowledges the re-apply.
        await addRequestDetailsToActivitySessionByRecipientTokenApi({
            inviteCode: inviteToken,
            requestType,
        });
        return true;
    } catch (error) {
        console.warn('[Request MFE] Invite-token recovery API call failed:', error);
        return false;
    }
};

const inferRequestTypeFromUrl = (): RequestType | null => {
    try {
        const location = globalThis.location.pathname + globalThis.location.search + globalThis.location.hash;
        const resolved = router.resolve(location);
        const requestTypeParam = (resolved.params.requestType as string) ?? '';
        return REQUEST_TYPE_MAP[requestTypeParam] ?? null;
    } catch {
        return null;
    }
};

const resolveEntryFromLocation = (): ResolvedEntry => {
    const location = globalThis.location.pathname + globalThis.location.search + globalThis.location.hash;
    const resolved = router.resolve(location);
    const token = (resolved.params.token as string) || '';
    const requestTypeParam = (resolved.params.requestType as string) ?? '';
    const requestType = REQUEST_TYPE_MAP[requestTypeParam] ?? null;
    const qrSessionToken = typeof resolved.query.qrSessionToken === 'string'
        ? resolved.query.qrSessionToken
        : '';
    const fromDashboard = resolved.query.source === 'dashboard';
    const enteredViaStartup = resolved.name === 'startup';
    // resolved.name is RouteRecordName | null | undefined (string | symbol | null | undefined);
    // narrow to the string case rather than casting away null/symbol.
    const isFlowContinuation = isFlowContinuationRoute(
        typeof resolved.name === 'string' ? resolved.name : null,
    );
    return { token, requestType, requestTypeParam, qrSessionToken, fromDashboard, enteredViaStartup, isFlowContinuation };
};

const applyQrSessionTokenIfAny = async (entry: ResolvedEntry): Promise<void> => {
    if (!entry.qrSessionToken || entry.requestType !== RequestType.Identity) return;
    useRequestSessionStore().setQrSessionToken(entry.qrSessionToken);
    const qrResult = await validateAndApplyQrSessionTokenApi({ qrSessionToken: entry.qrSessionToken });
    if (!qrResult?.valid) {
        console.warn('[Request MFE] QR session token invalid — OTP fallback will apply');
    }
};

/**
 * Handles entries that arrive with an `:token?` URL param.
 * @returns `true` when initialization should bail (a redirect has been issued).
 */
const handleInviteEntry = async (entry: ResolvedEntry): Promise<boolean> => {
    if (await maybeRedirectToInviteDashboard(entry)) return true;

    resetStores();

    // Re-apply after resetStores() clears all session state
    if (entry.qrSessionToken && entry.requestType === RequestType.Identity) {
        useRequestSessionStore().setQrSessionToken(entry.qrSessionToken);
    }

    // If entered at startup (from Dashboard), mark that welcome was skipped so
    // back navigation exits to Dashboard instead of landing on the welcome
    // primer the user never saw.
    if (entry.enteredViaStartup) {
        useRequestFlowStore().setSkippedWelcome();
    }

    await tryAddRequestDetailsByToken(entry);
    await tryRefreshUserIdentity();

    useRequestSessionStore().inviteToken = entry.token;
    return false;
};

const maybeRedirectToInviteDashboard = async (entry: ResolvedEntry): Promise<boolean> => {
    // Skip the stale-invite → dashboard redirect for non-fresh entries:
    //  - DS-2701: arrived from the dashboard MFE (source=dashboard) — avoid bouncing
    //    back to where the participant just came from.
    //  - PS-955: returning from an Intellicheck ID capture (match-idv-processing-complete
    //    / -verification-error). That callback carries no source=dashboard, so without
    //    this the participant gets redirected to the dashboard before ProcessingCompleteView
    //    can resolve the outcome and show the retry screen on a failed verification.
    if (shouldSkipInviteDashboardRedirect(entry)) return false;

    try {
        const redirectResult = await checkInviteRedirectApi(entry.token, entry.requestTypeParam);
        if (redirectResult?.shouldRedirect && redirectResult.redirectUrl) {
            const slug = getCompanySlug();
            const slugSegment = slug ? `/${slug}` : '';
            globalThis.location.href = `${slugSegment}${redirectResult.redirectUrl}`;
            return true;
        }
    } catch {
        // Redirect check failed — proceed with normal request flow
    }
    return false;
};

const tryAddRequestDetailsByToken = async (entry: ResolvedEntry): Promise<void> => {
    // The routes restrict `:requestType` to send|confirm|collect|identity, so a real entry
    // should always carry a non-null requestType. Guard anyway — a future route change
    // (or an unexpected entrypoint that bypasses the URL guard) shouldn't end up posting
    // `null` to the BFF, which would 400 and noisily land in logs.
    if (!entry.requestType) {
        console.warn('[Request MFE] tryAddRequestDetailsByToken skipped — requestType is null');
        return;
    }

    try {
        await addRequestDetailsToActivitySessionByRecipientTokenApi({
            inviteCode: entry.token,
            requestType: entry.requestType,
        });
    } catch (error) {
        console.warn('[Request MFE] Failed to add request details to session — continuing:', error);
    }
};

const tryRefreshUserIdentity = async (): Promise<void> => {
    try {
        await refreshUserIdentity();
    } catch (error) {
        console.warn('[Request MFE] Failed to refresh user identity — continuing:', error);
    }
};

const loadAndPublishLocation = async (): Promise<void> => {
    const locationStore = useLocationStore();
    await locationStore.loadLocation();
    setLocation(locationStore.location);
};

const resetStores = () => {
    useRequestSessionStore().resetForNewRequest();
    useRequestFlowStore().$reset();
    useRequestDetailsStore().$reset();
};
